/**
 * paramBus — the single seam between input and rendering.
 *
 * The input layer (keyState, modifiers, combos, tempo) is the ONLY thing that
 * calls `set()`. The rendering layer (Three.js scene, shader uniforms, UI) is
 * the ONLY thing that calls `get()` / `subscribe()`. Neither side imports the
 * other, so the whole 36-key config stays declarative and effects can be
 * added or rebalanced without touching input handling.
 *
 * A `set()` that doesn't change the value is a no-op: no log, no notify. That
 * keeps hold-effects (which may re-assert `1` every frame later) from spamming
 * subscribers, and makes the console change log a faithful record of *changes*.
 */

export type ParamValue = number;

export interface ParamChange {
  /** Namespaced parameter, e.g. `fx.bloom` or `dial.speed`. */
  readonly key: string;
  readonly value: ParamValue;
  /** Previous value, or `undefined` if this key had never been set. */
  readonly previous: ParamValue | undefined;
}

export type ParamSubscriber = (change: ParamChange) => void;

export interface ParamBusOptions {
  /** Log every change to the console (debugging aid). Default: true. */
  log?: boolean;
}

export class ParamBus {
  private readonly values = new Map<string, ParamValue>();
  private readonly subscribers = new Set<ParamSubscriber>();
  private logging: boolean;

  constructor(options: ParamBusOptions = {}) {
    this.logging = options.log ?? true;
  }

  /** Read a parameter. Returns `fallback` (default 0) if never set. */
  get(key: string, fallback: ParamValue = 0): ParamValue {
    const v = this.values.get(key);
    return v === undefined ? fallback : v;
  }

  has(key: string): boolean {
    return this.values.has(key);
  }

  /** Write a parameter. No-op (no log, no notify) if the value is unchanged. */
  set(key: string, value: ParamValue): void {
    const previous = this.values.get(key);
    if (previous === value) return;
    this.values.set(key, value);
    const change: ParamChange = { key, value, previous };
    // `mod.*` / `vmod.*` and `palette.hue` are continuous per-frame scrubs (like the
    // beat phase) — logging them would flood the console while +/- is held, so
    // they're kept out of the record.
    if (this.logging && !key.startsWith('mod.') && !key.startsWith('vmod.') && key !== 'palette.hue') {
      this.logChange(change);
    }
    for (const fn of this.subscribers) fn(change);
  }

  /** Subscribe to all changes. Returns an unsubscribe function. */
  subscribe(fn: ParamSubscriber): () => void {
    this.subscribers.add(fn);
    return () => {
      this.subscribers.delete(fn);
    };
  }

  /** A shallow copy of every known parameter — handy for UI and debugging. */
  snapshot(): Record<string, ParamValue> {
    return Object.fromEntries(this.values);
  }

  setLogging(on: boolean): void {
    this.logging = on;
  }

  private logChange(c: ParamChange): void {
    const prev = c.previous === undefined ? '—' : c.previous.toFixed(2);
    // eslint-disable-next-line no-console
    console.log(
      `%c[paramBus]%c ${c.key.padEnd(12)} ${prev} → ${c.value.toFixed(2)}`,
      'color:#8B31CC;font-weight:700',
      'color:inherit',
    );
  }
}
